import { motion } from 'framer-motion'
import { useCarousel } from '#app/hooks/use-carousel.ts'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './button.tsx'
import { Icon } from './icon.tsx'

interface Testimonial {
	id: string
	quote: string
	author: string
	location: string
}

interface TestimonialsCarouselProps {
	testimonials: Testimonial[]
	className?: string
	autoPlay?: boolean
	autoPlayInterval?: number
	indicators?: boolean
	controlsPosition?: 'sides' | 'bottom'
}

function TestimonialCard({
	testimonial,
	index,
	currentIndex,
	total,
}: {
	testimonial: Testimonial
	index: number
	currentIndex: number
	total: number
}) {
	// Calculate position relative to current
	const offset = index - currentIndex
	const isActive = offset === 0

	// Handle wrapping for circular carousel
	let normalizedOffset = offset
	if (offset > 1) normalizedOffset = offset - total
	if (offset < -1) normalizedOffset = offset + total

	// Only show active and adjacent cards
	const shouldShow = Math.abs(normalizedOffset) <= 1
	if (!shouldShow) return null

	// Calculate 3D positioning for card shuffling effect
	const getCardTransform = () => {
		if (isActive) {
			// Active card: front center with stacked effect
			return {
				x: 0,
				y: 0,
				z: 200, // Much further forward
				rotateY: 0,
				rotateX: 0,
				scale: 1,
				opacity: 1,
				zIndex: 100,
			}
		} else if (normalizedOffset === 1) {
			// Next card: behind and to the right
			return {
				x: 60,
				y: 15,
				z: -50, // Behind active card
				rotateY: -30,
				rotateX: 8,
				scale: 0.85,
				opacity: 0.6,
				zIndex: 50,
			}
		} else if (normalizedOffset === -1) {
			// Previous card: behind and to the left
			return {
				x: -60,
				y: 15,
				z: -50, // Behind active card
				rotateY: 30,
				rotateX: 8,
				scale: 0.85,
				opacity: 0.6,
				zIndex: 50,
			}
		}

		return {
			x: 0,
			y: 0,
			z: 0,
			rotateY: 0,
			rotateX: 0,
			scale: 1,
			opacity: 0,
			zIndex: 1,
		}
	}

	const transform = getCardTransform()

	return (
		<motion.div
			className="pointer-events-auto absolute inset-0 flex items-center justify-center"
			style={{
				zIndex: transform.zIndex,
			}}
			initial={false}
			animate={{
				x: transform.x,
				y: transform.y,
				z: transform.z,
				rotateY: transform.rotateY,
				rotateX: transform.rotateX,
				scale: transform.scale,
				opacity: transform.opacity,
			}}
			transition={{
				duration: 0.7,
				ease: [0.25, 0.46, 0.45, 0.94],
				type: 'spring',
				stiffness: 100,
				damping: 25,
			}}
		>
			<div
				className="relative h-[280px] w-[320px] md:h-[280px] md:w-[800px]"
				style={{ transformStyle: 'preserve-3d' }}
			>
				{/* Background stacked cards - only for active card */}
				{isActive && (
					<>
						{/* Third card back */}
						<motion.div
							className="shadow-2 absolute inset-0 rounded-2xl bg-neutral-100"
							style={{
								transform:
									'translateX(16px) translateY(6px) translateZ(-30px) scale(0.95)',
								width: '100%',
								height: '100%',
							}}
							initial={{ opacity: 0 }}
							animate={{ opacity: 0.6 }}
							transition={{ duration: 0.6, delay: 0.1 }}
						/>

						{/* Second card back - middle layer */}
						<motion.div
							className="shadow-1 absolute inset-0 rounded-2xl bg-neutral-50"
							style={{
								transform:
									'translateX(8px) translateY(3px) translateZ(-15px) scale(0.97)',
								width: '100%',
								height: '100%',
							}}
							initial={{ opacity: 0 }}
							animate={{ opacity: 0.4 }}
							transition={{ duration: 0.6, delay: 0.2 }}
						/>
					</>
				)}

				{/* Main testimonial card - Responsive dimensions */}
				<motion.div
					className="relative box-border flex size-full flex-col content-stretch items-center justify-center gap-6 overflow-hidden rounded-[16px] bg-white px-6 py-8 shadow-[0px_2px_4px_0px_#d9d9d9] md:px-12 md:py-12"
					style={{
						transformStyle: 'preserve-3d',
						width: '100%',
						height: '100%',
					}}
					whileHover={{
						scale: isActive ? 1.02 : 1,
						transition: { duration: 0.2 },
					}}
				>
					<div className="relative flex w-full shrink-0 flex-col content-stretch items-center justify-start gap-8">
						<div className="relative w-full max-w-none overflow-hidden">
							<p className="font-parkinsans line-clamp-4 text-center text-[16px] leading-[30px] font-normal text-[#054462] md:line-clamp-none">
								"{testimonial.quote}"
							</p>
						</div>
						<div className="relative flex shrink-0 flex-col content-stretch items-center justify-start text-center">
							<p className="font-parkinsans max-w-full truncate text-[16px] leading-[32px] font-bold text-[#054462]">
								{testimonial.author}
							</p>
							<p className="font-parkinsans max-w-full truncate text-[12px] leading-[19px] font-normal text-neutral-600">
								{testimonial.location}
							</p>
						</div>
					</div>
				</motion.div>
			</div>
		</motion.div>
	)
}

export function TestimonialsCarousel({
	testimonials,
	className,
	autoPlay = true,
	autoPlayInterval = 6000,
	indicators = false,
	controlsPosition = 'sides',
}: TestimonialsCarouselProps) {
	const {
		currentIndex,
		isTransitioning,
		goToNext,
		goToPrevious,
		goToSlide,
		canGoNext,
		canGoPrevious,
	} = useCarousel({
		totalSlides: testimonials.length,
		autoPlay,
		autoPlayInterval,
		loop: true,
	})

	return (
		<div
			className={cn(
				controlsPosition === 'bottom'
					? 'relative flex w-full shrink-0 flex-col content-stretch items-center justify-start gap-3'
					: 'relative mx-auto flex h-[274px] w-full max-w-[1200px] items-center justify-center overflow-visible',
				className,
			)}
		>
			{/* Navigation Buttons - Sides (current design) */}
			{controlsPosition === 'sides' && (
				<>
					<motion.div
						className="absolute top-1/2 left-0 z-50 -translate-y-1/2"
						whileHover={{ scale: 1.1 }}
						whileTap={{ scale: 0.95 }}
					>
						<Button
							onClick={goToPrevious}
							disabled={isTransitioning}
							variant="primary"
							appearance="ghost"
							size="m"
							iconOnly
							className="shadow-3 hover:shadow-4 h-12 w-12 rounded-full bg-white transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
							aria-label="Testimonio anterior"
						>
							<Icon name="arrow-left" className="h-5 w-5 text-blue-800" />
						</Button>
					</motion.div>

					<motion.div
						className="absolute top-1/2 right-0 z-50 -translate-y-1/2"
						whileHover={{ scale: 1.1 }}
						whileTap={{ scale: 0.95 }}
					>
						<Button
							onClick={goToNext}
							disabled={isTransitioning}
							variant="primary"
							appearance="ghost"
							size="m"
							iconOnly
							className="shadow-3 hover:shadow-4 h-12 w-12 rounded-full bg-white transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
							aria-label="Siguiente testimonio"
						>
							<Icon name="arrow-right" className="h-5 w-5 text-blue-800" />
						</Button>
					</motion.div>
				</>
			)}

			{/* 3D Testimonial Cards Container */}
			<div
				className={cn(
					'relative flex w-full items-center justify-center',
					controlsPosition === 'bottom' ? 'h-[274px]' : 'h-full',
				)}
				style={{
					perspective: '1200px',
					transformStyle: 'preserve-3d',
				}}
			>
				{testimonials.map((testimonial, index) => (
					<TestimonialCard
						key={testimonial.id}
						testimonial={testimonial}
						index={index}
						currentIndex={currentIndex}
						total={testimonials.length}
					/>
				))}
			</div>

			{/* Navigation Buttons - Bottom (Mobile design) */}
			{controlsPosition === 'bottom' && (
				<div className="relative mt-8 flex shrink-0 flex-row items-center justify-center gap-4">
					<motion.button
						onClick={goToPrevious}
						disabled={isTransitioning}
						// className="h-12 w-12 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						aria-label="Testimonio anterior"
					>
						<Icon
							name="arrow-left"
							className="h-5 w-5 cursor-pointer text-blue-800"
						/>
					</motion.button>

					<motion.button
						onClick={goToNext}
						disabled={isTransitioning}
						// className="h-12 w-12 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						aria-label="Siguiente testimonio"
					>
						<Icon
							name="arrow-right"
							className="h-5 w-5 cursor-pointer text-blue-800"
						/>
					</motion.button>
				</div>
			)}

			{/* Carousel Indicators */}
			{indicators ? (
				<div className="absolute bottom-6 left-1/2 z-50 flex -translate-x-1/2 gap-2">
					{testimonials.map((_, index) => (
						<motion.button
							key={index}
							className={cn(
								'h-2 w-2 rounded-full transition-all duration-300',
								index === currentIndex
									? 'scale-125 bg-blue-500'
									: 'bg-white/60 hover:bg-white/80',
							)}
							onClick={() => goToSlide(index)}
							disabled={isTransitioning}
							whileHover={{ scale: 1.2 }}
							whileTap={{ scale: 0.9 }}
							aria-label={`Ir al testimonio ${index + 1}`}
						/>
					))}
				</div>
			) : undefined}
		</div>
	)
}

export default TestimonialsCarousel
